import * as React from 'react';
import { Button } from '@fluentui/react-components';
import { ChevronDoubleLeftRegular, ChevronLeftRegular, ChevronRightRegular, ChevronDoubleRightRegular } from '@fluentui/react-icons';
import { PageNumber } from './PageNumber';
import { ComboBox } from '@fluentui/react/lib/ComboBox';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import type { IPaginationProps, IPaginationString } from './Pagination.types';
import type { IComboBoxOption, IComboBox } from '@fluentui/react/lib/ComboBox';

//const getClassNames = classNamesFunction<IPaginationStyleProps, IPaginationStyles>();

const DEFAULT_STRINGS: IPaginationString = {
  of: 'of',
  divider: '-',
};

export class PaginationBase extends React.Component<IPaginationProps> {
  public static defaultProps: Partial<IPaginationProps> = {
    selectedPageIndex: 0,
    format: 'comboBox',
    numberOfPageButton: 5,
    previousPageIconProps: { iconName: 'CaretSolidLeft' },
    nextPageIconProps: { iconName: 'CaretSolidRight' },
    firstPageIconProps: { iconName: 'Previous' },
    lastPageIconProps: { iconName: 'Next' },
    strings: DEFAULT_STRINGS,
  };

  //private _classNames: IProcessedStyleSet<IPaginationStyles>|null = null;

//   constructor(props: IPaginationProps) {
//     super(props);

// //    initializeComponentRef(this);
//   }

  public render(): JSX.Element {
    const {
      comboBoxAriaLabel,
      firstPageAriaLabel,
      previousPageAriaLabel,
      nextPageAriaLabel,
      lastPageAriaLabel,
      pageAriaLabel,
      pageCount,
      selectedPageIndex,
      onRenderVisibleItemLabel = this._renderVisibleItemLabel,
      format,
    } = this.props;

    // this._classNames = getClassNames(styles!, {
    //   theme: theme!,
    //   format: format,
    // });

    const canPrevious = selectedPageIndex! > 0;
    const canNext = selectedPageIndex! + 1 < pageCount;
    const canFirst = selectedPageIndex !== 0;
    const canLast = selectedPageIndex !== pageCount - 1;

    if (format === 'comboBox') {
      const scaleOptions: IComboBoxOption[] = [];

      for (let i = 0; i < this.props.pageCount; i++) {
        scaleOptions.push({
          key: `${i}`,
          text: `${i + 1}`,
        });
      }

      return (
        <div >
          <Button
            onClick={this._handleFirstPage}
            disabled={!canFirst}
            aria-label={firstPageAriaLabel}
            icon={<ChevronDoubleLeftRegular />}
          />
          <Button
            onClick={this._handlePreviousPage}
            disabled={!canPrevious}
            aria-label={previousPageAriaLabel}
            icon={<ChevronLeftRegular />}
          />
          <ComboBox
            ariaLabel={comboBoxAriaLabel}
            selectedKey={`${selectedPageIndex}`}
            options={scaleOptions}
            onChange={(e,o,i)=>this._onComboBoxChange(e, o as IComboBoxOption,i as number)}
            styles={{
              
            }}
          />
          <Button
            onClick={this._handleNextPage}
            disabled={!canNext}
            aria-label={nextPageAriaLabel}
            icon={<ChevronRightRegular />}
          />
          <Button
            onClick={this._handleLastPage}
            disabled={!canLast}
            aria-label={lastPageAriaLabel}
            icon={<ChevronDoubleRightRegular />}
          />
        </div>
      );
    }

    return (
      <div >
        <div>
          <Button
            onClick={this._handleFirstPage}
            disabled={!canFirst}
            aria-label={firstPageAriaLabel}
            shape='circular'
            icon={<ChevronDoubleLeftRegular />}

          />
          <Button
            onClick={this._handlePreviousPage}
            disabled={!canPrevious}
            aria-label={previousPageAriaLabel}
            shape='circular'
            icon={<ChevronLeftRegular />}
          />
          {this._pageList()}
          <Button
            onClick={this._handleNextPage}
            disabled={!canNext}
            aria-label={nextPageAriaLabel}
            shape='circular'
            icon={<ChevronRightRegular />}
          />
          <TooltipHost
            content={`${pageAriaLabel ? pageAriaLabel + ' ' : ''}${pageCount}`}
            directionalHint={DirectionalHint.bottomCenter}
          >
            <Button
              onClick={this._handleLastPage}
              icon={<ChevronDoubleRightRegular />}
              disabled={!canLast}
              shape='circular'
              aria-label={lastPageAriaLabel}
            />
          </TooltipHost>
        </div>
        {onRenderVisibleItemLabel(this.props)}
      </div>
    );
  }

  private _handleFirstPage = () => {
    this._handleSelectedPage(0);
  };

  private _handleLastPage = () => {
    this._handleSelectedPage(this.props.pageCount - 1);
  };

  private _onComboBoxChange = (_: React.FormEvent<IComboBox>, option: IComboBoxOption, index: number) => {
    if (option !== undefined) {
      this._handleSelectedPage(index);
    }
  };

  private _handleSelectedPage = (selected: number) => {
    const { selectedPageIndex, onPageChange } = this.props;
    if (selected === selectedPageIndex) {
      return; // same page, no action
    }
    if (onPageChange) {
      onPageChange(selected);
    }
  };

  private _handlePreviousPage = () => {
    this._handleSelectedPage(this.props.selectedPageIndex! - 1);
  };

  private _handleNextPage = () => {
    this._handleSelectedPage(this.props.selectedPageIndex! + 1);
  };

  private _pageElement(index: number): JSX.Element {
    const { pageAriaLabel, pageCount, selectedPageIndex, selectedAriaLabel, strings } = this.props;
    const isSelected = index === selectedPageIndex;
    let ariaLabel = pageAriaLabel && `${pageAriaLabel} ${index + 1} ${strings!.of} ${pageCount}`;

    if (isSelected) {
      ariaLabel = ariaLabel + ' ' + selectedAriaLabel;
    }

    return (
      <PageNumber
        key={index + 1}
        page={index + 1}
        ariaLabel={ariaLabel}
        selected={isSelected}
        onClick={this._handleSelectedPage}
        //className={this._classNames?.pageNumber}
      />
    );
  }

  private _pageList(): JSX.Element[] {
    const { numberOfPageButton, pageCount, selectedPageIndex } = this.props;
    const pageList :JSX.Element[]= [];
    if (pageCount <= numberOfPageButton!) {
      for (let index = 0; index < pageCount; index++) {
        pageList.push(this._pageElement(index));
      }
    } else {
      const leftHalfCount = Math.floor((numberOfPageButton! - 1) / 2);
      const rightHalfCount = numberOfPageButton! - 1 - leftHalfCount;
      let leftSide = selectedPageIndex! - leftHalfCount;
      let rightSide = selectedPageIndex! + rightHalfCount;
      if (rightSide > pageCount - 1) {
        rightSide = pageCount - 1;
        leftSide = rightSide - numberOfPageButton! + 1;
      } else if (leftSide < 0) {
        leftSide = 0;
        rightSide = numberOfPageButton! - 1;
      }
      for (let index = leftSide; index <= rightSide; index++) {
        pageList.push(this._pageElement(index));
      }
    }

    return pageList;
  }

  private _renderVisibleItemLabel = (props: IPaginationProps): JSX.Element | null => {
    if (props.onRenderVisibleItemLabel) {
      return <div >{props.onRenderVisibleItemLabel(props)}</div>;
    }

    if (props.itemsPerPage && props.totalItemCount) {
      const leftItemIndex = props.selectedPageIndex! * props.itemsPerPage + 1;
      const rightItemsIndex = Math.min((props.selectedPageIndex! + 1) * props.itemsPerPage, props.totalItemCount);
      const visibleItemLabel = `${leftItemIndex} ${props.strings!.divider} ${rightItemsIndex} ${props.strings!.of} ${
        props.totalItemCount
      }`;
      return (
        <div aria-label={visibleItemLabel}>
          {visibleItemLabel}
        </div>
      );
    }

    return null;
  };
}